import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Upload, X, Send, CheckCircle2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { FORM_SERVICE_VALUES } from "../config/services";

// ====================================================================
// CONTACT FORM
// --------------------------------------------------------------------
// Submits to POST {REACT_APP_BACKEND_URL}/api/quotes
//
// To connect this form to an email inbox, webhook, or CRM later, swap
// the API URL below or extend the backend /api/quotes route (see
// /app/backend/server.py).
// ====================================================================

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = FORM_SERVICE_VALUES;

const inputCls =
  "w-full bg-white border border-[#DCD7CE] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A90] focus:ring-2 focus:ring-[#7A2D2F]/30 focus:border-[#7A2D2F] focus:outline-none transition-all";

export default function ContactForm({ defaultService = "" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    suburb: "",
    service: defaultService,
    message: "",
  });
  const [photo, setPhoto] = useState(null); // { name, dataUrl }
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function onPhoto(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setError("Photo must be under 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto({ name: f.name, dataUrl: reader.result });
      setError("");
    };
    reader.readAsDataURL(f);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) return setError("Please enter your name.");
    if (!form.phone.trim()) return setError("Please enter your phone number.");
    if (!/^\S+@\S+\.\S+$/.test(form.email))
      return setError("Please enter a valid email address.");
    if (!form.service) return setError("Please select a service.");

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        suburb: form.suburb.trim(),
        service: form.service,
        message: form.message.trim(),
        photo_base64: photo?.dataUrl || null,
        photo_name: photo?.name || null,
      };
      await axios.post(`${API}/quotes`, payload);
      setSuccess(true);
      setForm({
        name: "",
        phone: "",
        email: "",
        suburb: "",
        service: "",
        message: "",
      });
      setPhoto(null);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.detail ||
          "Something went wrong while sending your request. Please try again or call us directly."
      );
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div
        className="bg-white border border-[#DCD7CE] rounded-3xl p-10 text-center shadow-[0_20px_50px_rgba(20,40,31,0.06)]"
        data-testid="form-success"
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-[#F3E8E5] flex items-center justify-center mb-5">
          <CheckCircle2 className="text-[#7A2D2F]" size={28} />
        </div>
        <h3
          className="text-2xl text-[#1A1A1A] mb-2"
          style={{ fontFamily: "'Fraunces', serif" }}
        >
          Your request is on the way.
        </h3>
        <p className="text-[#4A4D4A] max-w-md mx-auto">
          Thanks for reaching out — we’ll get back to you shortly with next
          steps. For urgent jobs, feel free to call us directly.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className="mt-6 text-[#7A2D2F] underline underline-offset-4 text-sm font-semibold"
          data-testid="form-send-another"
        >
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-[#DCD7CE] rounded-3xl p-6 md:p-10 shadow-[0_20px_50px_rgba(20,40,31,0.06)]"
      data-testid="contact-form"
    >
      <h3
        className="text-2xl md:text-3xl text-[#1A1A1A] mb-1 tracking-tight"
        style={{ fontFamily: "'Fraunces', serif" }}
      >
        Request a quote
      </h3>
      <p className="text-[#4A4D4A] text-sm mb-6">
        Fill in a few details and we’ll get back to you with pricing and
        timing.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[13px] font-medium text-[#1A1A1A] mb-2">
            Name
          </label>
          <input
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your full name"
            className={inputCls}
            data-testid="form-name"
          />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-[#1A1A1A] mb-2">
            Phone
          </label>
          <input
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="04xx xxx xxx"
            className={inputCls}
            data-testid="form-phone"
          />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-[#1A1A1A] mb-2">
            Email
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@example.com"
            className={inputCls}
            data-testid="form-email"
          />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-[#1A1A1A] mb-2">
            Suburb
          </label>
          <input
            value={form.suburb}
            onChange={(e) => update("suburb", e.target.value)}
            placeholder="e.g. Brunswick"
            className={inputCls}
            data-testid="form-suburb"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-[13px] font-medium text-[#1A1A1A] mb-2">
          Service required
        </label>
        <Select
          value={form.service}
          onValueChange={(v) => update("service", v)}
        >
          <SelectTrigger
            className="w-full h-[50px] bg-white border-[#DCD7CE] rounded-xl text-[#1A1A1A] data-[placeholder]:text-[#9A9A90] focus:ring-2 focus:ring-[#7A2D2F]/30"
            data-testid="form-service-trigger"
          >
            <SelectValue placeholder="Select a service…" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem
                key={s}
                value={s}
                data-testid={`form-service-option-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4">
        <label className="block text-[13px] font-medium text-[#1A1A1A] mb-2">
          Project details
        </label>
        <textarea
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          rows={5}
          placeholder="Tell us about your space, size, timing, and anything we should know."
          className={`${inputCls} resize-none`}
          data-testid="form-message"
        />
      </div>

      <div className="mt-4">
        <label className="block text-[13px] font-medium text-[#1A1A1A] mb-2">
          Upload a photo (optional)
        </label>
        {photo ? (
          <div
            className="flex items-center justify-between border border-[#DCD7CE] rounded-xl p-3 bg-[#F8F7F4]"
            data-testid="form-photo-preview"
          >
            <div className="flex items-center gap-3">
              <img
                src={photo.dataUrl}
                alt="Preview"
                className="h-12 w-12 object-cover rounded-md"
              />
              <span className="text-sm text-[#1A1A1A] truncate max-w-[180px]">
                {photo.name}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setPhoto(null)}
              className="p-2 text-[#4A4D4A] hover:text-[#5E2023]"
              aria-label="Remove photo"
              data-testid="form-photo-remove"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <label
            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#DCD7CE] rounded-xl py-8 px-4 bg-[#F8F7F4] text-[#4A4D4A] text-sm cursor-pointer hover:border-[#7A2D2F] hover:text-[#7A2D2F] transition-colors"
            data-testid="form-photo-dropzone"
          >
            <Upload size={20} />
            <span className="font-medium">Click to upload a photo</span>
            <span className="text-xs text-[#9A9A90]">
              JPG or PNG up to 5MB
            </span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPhoto}
              data-testid="form-photo-input"
            />
          </label>
        )}
      </div>

      {error && (
        <p
          className="mt-4 text-sm text-[#5E2023]"
          role="alert"
          data-testid="form-error"
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="mt-6 inline-flex items-center justify-center gap-2 w-full md:w-auto bg-[#7A2D2F] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#0D0D0E] transition-all hover:-translate-y-0.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="form-submit"
      >
        {loading ? "Sending…" : "Request my quote"}
        {!loading && <Send size={16} />}
      </button>
      <p className="mt-3 text-[11px] text-[#9A9A90] leading-relaxed">
        By submitting, you agree to our{" "}
        <Link
          to="/privacy"
          className="underline hover:text-[#7A2D2F]"
          data-testid="contact-privacy-link"
        >
          Privacy Policy
        </Link>
        . We&#39;ll only use your details to get back to you about your project.
      </p>
    </form>
  );
}
